import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, X, ArrowUpRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import CTASection from "@/sections/CTASection";
import { PROJECTS } from "@/data/projects";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";

const HERO_IMG = "https://images.pexels.com/photos/11310491/pexels-photo-11310491.jpeg?w=2400";

const CATEGORIES = ["All", ...Array.from(new Set(PROJECTS.map((p) => p.category)))];

export default function Projects() {
  const [filter, setFilter] = useState("All");
  const [open, setOpen] = useState(null);
  const filtered = filter === "All" ? PROJECTS : PROJECTS.filter((p) => p.category === filter);

  return (
    <div data-testid="projects-page">
      <PageHero
        overline="Selected work"
        title={<>Projects that<br /><span className="italic font-light text-gold">stand the test of time.</span></>}
        subtitle="From mobility corridors and smart cities to luxury resorts, retail destinations and cultural landmarks."
        image={HERO_IMG}
        breadcrumbs={[{ label: "Projects" }]}
      />

      <section className="bg-white py-24 sm:py-32">
        <div className="container-x">
          <div className="flex flex-wrap gap-2 mb-12" data-testid="projects-filters">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                data-testid={`project-filter-${c.toLowerCase()}`}
                className={`px-4 py-2 text-xs tracking-[0.2em] uppercase font-semibold border transition-colors ${
                  filter === c
                    ? "bg-navy text-white border-navy"
                    : "bg-white text-slate-700 border-slate-300 hover:border-navy"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence mode="popLayout">
              {filtered.map((p) => (
                <motion.button
                  key={p.slug}
                  layout
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -18 }}
                  transition={{ duration: 0.4 }}
                  onClick={() => setOpen(p)}
                  className="group relative aspect-[4/5] overflow-hidden bg-navy text-left"
                  data-testid={`projects-page-card-${p.slug}`}
                >
                  <img src={p.cover} alt={p.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-transparent opacity-90" />
                  <div className="relative h-full flex flex-col justify-between p-6 sm:p-8 text-white">
                    <div className="flex items-start justify-between">
                      <span className="overline px-3 py-1 bg-white/10 backdrop-blur border border-white/20">{p.category}</span>
                      <ArrowUpRight className="opacity-0 group-hover:opacity-100 transition-opacity" size={20} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 text-xs text-white/70">
                        <MapPin size={12} className="text-gold" />
                        {p.location}
                      </div>
                      <h3 className="mt-2 font-display font-bold text-xl sm:text-2xl leading-tight">{p.title}</h3>
                    </div>
                  </div>
                </motion.button>
              ))}
            </AnimatePresence>
          </div>
        </div>
      </section>

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-5xl p-0 overflow-hidden border-0" data-testid="project-detail-dialog">
          {open && (
            <div>
              <DialogTitle className="sr-only">{open.title}</DialogTitle>
              <div className="relative aspect-[16/9] bg-navy">
                <img src={open.cover} alt={open.title} className="w-full h-full object-cover" />
                <button
                  onClick={() => setOpen(null)}
                  className="absolute top-4 right-4 w-10 h-10 grid place-items-center bg-white/90 text-navy"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
                <div className="absolute inset-0 bg-gradient-to-t from-navy/80 to-transparent" />
                <div className="absolute bottom-0 inset-x-0 p-8 text-white">
                  <span className="overline text-gold">{open.category}</span>
                  <h3 className="font-display font-extrabold text-3xl sm:text-4xl mt-2">{open.title}</h3>
                  <div className="flex items-center gap-2 mt-2 text-xs">
                    <MapPin size={12} className="text-gold" />
                    {open.location} · {open.timeline}
                  </div>
                </div>
              </div>
              <div className="p-8 grid lg:grid-cols-3 gap-8 bg-white">
                <div className="lg:col-span-2 space-y-6">
                  <div>
                    <div className="overline text-emerald-brand">Challenges</div>
                    <p className="mt-2 text-slate-brand leading-relaxed">{open.challenges}</p>
                  </div>
                  <div>
                    <div className="overline text-emerald-brand">Solutions</div>
                    <p className="mt-2 text-slate-brand leading-relaxed">{open.solutions}</p>
                  </div>
                  <div>
                    <div className="overline text-emerald-brand">Outcome</div>
                    <p className="mt-2 text-slate-brand leading-relaxed">{open.outcome}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <div className="overline text-emerald-brand">Client</div>
                    <div className="font-display font-bold text-navy mt-1">{open.client}</div>
                  </div>
                  <div>
                    <div className="overline text-emerald-brand">Services</div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {open.services.map((s) => (
                        <span key={s} className="text-xs px-2 py-1 border border-slate-200 text-slate-700">{s}</span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="overline text-emerald-brand">Specs</div>
                    <div className="mt-2 space-y-2">
                      {open.specs.map((sp) => (
                        <div key={sp.k} className="flex justify-between text-sm border-b border-slate-200 pb-2">
                          <span className="text-slate-500">{sp.k}</span>
                          <span className="font-display font-semibold text-navy">{sp.v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <CTASection />
    </div>
  );
}
